import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { List } from 'immutable';

// hooks
import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from 'helpers/hooks/useDebounce';

// components
import {
  Mention,
  MentionsInput,
  MentionsInputProps,
  SuggestionDataItem,
} from 'react-mentions';

// selectors
import { fetchMembersForGroup as fetchMembersForGroupAction } from 'store/actions/memberActions';
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getMembers from 'store/selectors/getMembers';

// styles
import theme from 'assets/theme';
import { alpha } from '@material-ui/core/styles';

export type MemberMentionProps = Omit<MentionsInputProps, 'children'>;

export default function MemberMention(props: MemberMentionProps) {
  const dispatch = useDispatch();
  const currentGroupId = useSelector(getCurrentGroupId);
  const fetchMembersForGroup = useCallback(
    (payload: { groupId: number; search: string }) =>
      dispatch(fetchMembersForGroupAction(payload)),
    [dispatch]
  );
  const members = (
    useSelector(getMembers('member'), isEqual) || List()
  ).toJS() as Member[];

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const mentionCallbackRef = useRef<
    ((data: SuggestionDataItem[]) => void) | null
  >(null);
  const mentionData: SuggestionDataItem[] = useMemo(
    () =>
      members.map(({ id, account: { firstName, lastName } }: Member) => ({
        id: id,
        display: `${firstName} ${lastName}`,
      })),
    [members]
  );

  const fetchUsers = useCallback(
    (search: string, callback: (data: SuggestionDataItem[]) => void) => {
      mentionCallbackRef.current = callback;
      setSearchTerm(search);
      if (!search && mentionData.length > 0) {
        callback(mentionData);
      }
    },
    [mentionData]
  );

  useEffect(() => {
    fetchMembersForGroup({
      groupId: currentGroupId,
      search: debouncedSearchTerm,
    });
  }, [debouncedSearchTerm, fetchMembersForGroup, currentGroupId]);

  useEffect(() => {
    if (mentionCallbackRef.current && mentionData.length) {
      mentionCallbackRef.current(mentionData);
    }
  }, [mentionData]);

  return (
    <MentionsInput {...props}>
      <Mention
        displayTransform={(_, display) => `@${display}`}
        style={mentionsStyle}
        trigger='@'
        data={fetchUsers}
        onAdd={() => {
          mentionCallbackRef.current = null;
          setSearchTerm('');
        }}
      />
    </MentionsInput>
  );
}

const mentionsStyle = {
  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
  color: theme.palette.secondary.main,
  borderRadius: 6,
};
